import { agencyNavigationGroups } from './shigo-navigation.agency';
import { ShigoDashboardContext } from '../../features/auth/dashboardDestination';
import { NetworkAvailableFeature } from '../../features/auth/networkFeatures';
import { ShigoPermission } from '../../features/auth/permissions';
import { adsTxtNavigationEntry } from './shigo-navigation.adsTxt';
import { shigoReportingNavigationGroup } from './shigo-navigation.reporting';
import {
  ShigoNavigationItem,
  type ShigoNavigationAccess,
  type ShigoNavigationAccessContext,
  type ShigoNavigationEntry,
  type ShigoNavigationGroup,
  type VisibleShigoNavigationEntry,
  type VisibleShigoNavigationGroup,
} from './shigo-navigation.types';

export type { ShigoNavigationGroupItem } from './shigo-navigation.types';

const mainDashboardAccess = { contexts: [ShigoDashboardContext.Main] } as const;

export const shigoNavigationLinks: readonly ShigoNavigationEntry[] = [
  {
    id: ShigoNavigationItem.Networks,
    labelKey: 'nav.admin.networks',
    to: '/networks',
    activePaths: ['/networks'],
    access: [{ contexts: [ShigoDashboardContext.SuperAdmin] }],
  },
  {
    id: ShigoNavigationItem.Debug,
    labelKey: 'nav.debug',
    to: '/debug',
    activePaths: ['/debug'],
    access: [
      {
        ...mainDashboardAccess,
        permissions: [ShigoPermission.DebugRead],
      },
    ],
  },
];

export const shigoNavigationGroups: readonly ShigoNavigationGroup[] = [
  ...agencyNavigationGroups,
  {
    id: ShigoNavigationItem.Supply,
    labelKey: 'nav.supply',
    activePaths: ['/supply'],
    access: [
      {
        ...mainDashboardAccess,
        permissions: [ShigoPermission.SupplyRead],
      },
      {
        ...mainDashboardAccess,
        permissions: [ShigoPermission.SupplyPlacementsRead],
      },
      {
        ...mainDashboardAccess,
        permissions: [ShigoPermission.BlocklistCreativesRead],
      },
      {
        ...mainDashboardAccess,
        permissions: [ShigoPermission.SupplyListsRead],
      },
      {
        ...mainDashboardAccess,
        permissions: [ShigoPermission.SupplyAppsRead],
      },
      {
        ...mainDashboardAccess,
        permissions: [ShigoPermission.DemandComplianceRead],
      },
      {
        ...mainDashboardAccess,
        permissions: [ShigoPermission.SupplyEndpointGeneratorRead],
      },
      {
        ...mainDashboardAccess,
        permissions: [ShigoPermission.TargetingRead],
      },
      {
        ...mainDashboardAccess,
        permissions: [ShigoPermission.SupplyAdsTxtRead],
      },
    ],
    entries: [
      {
        id: ShigoNavigationItem.SupplyPublishers,
        labelKey: 'nav.supply.publishers',
        to: '/supply/publishers',
        activePaths: ['/supply/publishers'],
        access: [
          {
            permissions: [ShigoPermission.SupplyRead, ShigoPermission.SupplyPublishersRead],
          },
        ],
      },
      {
        id: ShigoNavigationItem.SupplyPlacements,
        labelKey: 'nav.supply.placements',
        to: '/supply/placements',
        activePaths: ['/supply/placements'],
        access: [{ permissions: [ShigoPermission.SupplyPlacementsRead] }],
      },
      {
        id: ShigoNavigationItem.SupplyApps,
        labelKey: 'nav.supply.apps',
        to: ({ can }) =>
          can(ShigoPermission.SupplyAppsInventoryRead) ? '/supply/apps' : '/supply/apps/requests',
        activePaths: ['/supply/apps'],
        access: [
          {
            permissions: [ShigoPermission.SupplyAppsRead, ShigoPermission.SupplyAppsInventoryRead],
          },
          {
            permissions: [ShigoPermission.SupplyAppsRead, ShigoPermission.SupplyAppRequestsRead],
          },
        ],
      },
      {
        id: ShigoNavigationItem.SupplyLists,
        labelKey: 'nav.supply.lists',
        to: '/supply/lists/all',
        activePaths: ['/supply/lists'],
        access: [
          { permissions: [ShigoPermission.SupplyListsRead] },
          { permissions: [ShigoPermission.SupplyAppsRead] },
        ],
      },
      {
        id: ShigoNavigationItem.SupplyDomains,
        labelKey: 'nav.supply.domains',
        to: '/supply/domains',
        activePaths: ['/supply/domains'],
        access: [{ permissions: [ShigoPermission.DemandComplianceRead] }],
      },
      {
        id: ShigoNavigationItem.SupplyTailoredSupply,
        labelKey: 'nav.supply.tailoredSupply',
        to: '/supply/tailored-supply',
        activePaths: ['/supply/tailored-supply'],
        access: [{ permissions: [ShigoPermission.TargetingRead] }],
      },
      adsTxtNavigationEntry,
      {
        id: ShigoNavigationItem.SupplyBlockedList,
        labelKey: 'nav.supply.blockedList',
        to: '/supply/blocked-list/creatives',
        activePaths: ['/supply/blocked-list'],
        access: [{ permissions: [ShigoPermission.BlocklistCreativesRead] }],
      },
      {
        id: ShigoNavigationItem.SupplyEndpointGenerator,
        labelKey: 'nav.supply.endpointGenerator',
        to: '/supply/endpoint-generator',
        activePaths: ['/supply/endpoint-generator'],
        access: [{ permissions: [ShigoPermission.SupplyEndpointGeneratorRead] }],
      },
    ],
  },
  {
    id: ShigoNavigationItem.Demand,
    labelKey: 'nav.demand',
    activePaths: ['/demand'],
    access: [
      {
        ...mainDashboardAccess,
        permissions: [ShigoPermission.DemandRead],
      },
    ],
    entries: [
      {
        id: ShigoNavigationItem.DemandOwners,
        labelKey: 'nav.demand.owners',
        to: '/demand/owners',
        activePaths: ['/demand/owners'],
        access: [
          {
            permissions: [ShigoPermission.DemandPartnersRead],
            networkFeatures: [NetworkAvailableFeature.Owner],
          },
        ],
      },
      {
        id: ShigoNavigationItem.DemandPartners,
        labelKey: 'nav.demand.partnersMenu',
        to: '/demand/partners',
        activePaths: ['/demand/partners'],
        access: [{ permissions: [ShigoPermission.DemandPartnersRead] }],
      },
      {
        id: ShigoNavigationItem.DemandIntegrations,
        labelKey: 'nav.demand.integrations',
        to: '/demand/integrations',
        activePaths: ['/demand/integrations'],
        access: [{ permissions: [ShigoPermission.DemandIntegrationsRead] }],
      },
      {
        id: ShigoNavigationItem.DemandDeals,
        labelKey: 'nav.demand.deals',
        to: '/demand/deals',
        activePaths: ['/demand/deals'],
        access: [{ permissions: [ShigoPermission.DemandDealsRead] }],
      },
    ],
  },
  {
    id: ShigoNavigationItem.Campaigns,
    labelKey: 'nav.campaigns',
    activePaths: ['/campaigns'],
    access: [
      {
        ...mainDashboardAccess,
        networkFeatures: [NetworkAvailableFeature.Campaign],
      },
    ],
    entries: [
      {
        id: ShigoNavigationItem.CampaignAgency,
        labelKey: 'nav.campaigns.agency',
        to: '/campaigns/agencies',
        activePaths: ['/campaigns/agencies'],
        access: [{ ...mainDashboardAccess, permissions: [ShigoPermission.SupplyRead] }],
      },
      {
        id: ShigoNavigationItem.CampaignBrands,
        labelKey: 'nav.campaigns.brands',
        to: '/campaigns/brands',
        activePaths: ['/campaigns/brands'],
        access: [{ permissions: [ShigoPermission.MadBuyCampaignRead] }],
      },
      {
        id: ShigoNavigationItem.CampaignCampaigns,
        labelKey: 'nav.campaigns.campaigns',
        to: '/campaigns/campaigns',
        activePaths: ['/campaigns/campaigns'],
        access: [{ permissions: [ShigoPermission.MadBuyCampaignRead] }],
      },
      {
        id: ShigoNavigationItem.CampaignCreatives,
        labelKey: 'nav.campaigns.creatives',
        to: '/campaigns/creatives',
        activePaths: ['/campaigns/creatives'],
        access: [{ permissions: [ShigoPermission.MadBuyCreativesRead] }],
      },
      {
        id: ShigoNavigationItem.CampaignSupply,
        labelKey: 'nav.campaigns.supply',
        to: '/campaigns/supply/apps',
        activePaths: ['/campaigns/supply'],
        access: [{ permissions: [ShigoPermission.MadBuySupplyRead] }],
      },
      {
        id: ShigoNavigationItem.CampaignChannelGroups,
        labelKey: 'nav.campaigns.channelGroups',
        to: '/campaigns/channel-groups',
        activePaths: ['/campaigns/channel-groups'],
        access: [{ permissions: [ShigoPermission.MadBuyCampaignRead] }],
      },
    ],
  },
  shigoReportingNavigationGroup,
  {
    id: ShigoNavigationItem.A2v,
    labelKey: 'nav.a2v',
    activePaths: ['/a2v'],
    access: [
      {
        ...mainDashboardAccess,
        permissions: [ShigoPermission.A2vRead],
        networkFeatures: [NetworkAvailableFeature.A2v],
      },
    ],
    entries: [
      {
        id: ShigoNavigationItem.A2vAssets,
        labelKey: 'nav.a2v.assets',
        to: '/a2v/assets',
        activePaths: ['/a2v/assets'],
        access: [{ permissions: [ShigoPermission.A2vAssetsRead] }],
      },
      {
        id: ShigoNavigationItem.A2vNetworkTemplate,
        labelKey: 'nav.a2v.networkTemplate',
        to: '/a2v/network-template',
        activePaths: ['/a2v/network-template'],
        access: [{ permissions: [ShigoPermission.A2vNetworkTemplateRead] }],
      },
    ],
  },
  {
    id: ShigoNavigationItem.Targeting,
    labelKey: 'nav.targeting',
    activePaths: ['/targeting'],
    access: [
      {
        ...mainDashboardAccess,
        permissions: [ShigoPermission.TargetingRead],
      },
    ],
    entries: [
      {
        id: ShigoNavigationItem.TargetingBuilder,
        labelKey: 'nav.targeting.builder',
        to: '/targeting',
        activePaths: ['/targeting'],
      },
    ],
  },
  {
    id: ShigoNavigationItem.Admin,
    labelKey: 'nav.admin',
    activePaths: ['/admin'],
    access: [
      {
        contexts: [ShigoDashboardContext.Main, ShigoDashboardContext.SuperAdmin],
      },
    ],
    entries: [
      {
        id: ShigoNavigationItem.AdminChangelog,
        labelKey: 'nav.admin.changelog',
        to: '/admin/changelog',
        activePaths: ['/admin/changelog'],
        access: [
          {
            contexts: [ShigoDashboardContext.Main],
            permissions: [ShigoPermission.AdminChangelogRead],
          },
        ],
      },
      {
        id: ShigoNavigationItem.AdminUsers,
        labelKey: 'nav.admin.users',
        to: '/admin/users',
        activePaths: ['/admin/users'],
        access: [
          { contexts: [ShigoDashboardContext.SuperAdmin] },
          {
            contexts: [ShigoDashboardContext.Main],
            permissions: [ShigoPermission.AdminUsersRead],
          },
        ],
      },
      {
        id: ShigoNavigationItem.AdminStitcherAccess,
        labelKey: 'nav.admin.stitcherAccess',
        to: '/admin/stitcher-access',
        activePaths: ['/admin/stitcher-access'],
        access: [{ contexts: [ShigoDashboardContext.SuperAdmin] }],
      },
    ],
  },
];

export function resolveVisibleNavigationLinks(
  links: readonly ShigoNavigationEntry[],
  context: ShigoNavigationAccessContext,
): VisibleShigoNavigationEntry[] {
  return links.flatMap((link) => {
    if (!canAccessNavigation(link.access, context)) return [];
    return [{ ...link, to: typeof link.to === 'function' ? link.to(context) : link.to }];
  });
}

export function resolveVisibleNavigationGroups(
  groups: readonly ShigoNavigationGroup[],
  context: ShigoNavigationAccessContext,
): VisibleShigoNavigationGroup[] {
  return groups.flatMap((group) => {
    if (!canAccessNavigation(group.access, context)) return [];
    const entries = group.entries.flatMap((entry) => {
      if (!canAccessNavigation(entry.access, context)) return [];
      return [{ ...entry, to: typeof entry.to === 'function' ? entry.to(context) : entry.to }];
    });
    return entries.length > 0 ? [{ ...group, entries }] : [];
  });
}

export function isNavigationPathActive(activePaths: readonly string[], pathname: string) {
  return activePaths.some((activePath) => {
    if (!activePath.includes('$')) {
      return pathname === activePath || pathname.startsWith(`${activePath}/`);
    }
    const pattern = activePath
      .split('/')
      .map((segment) => (segment.startsWith('$') ? '[^/]+' : escapeRegExp(segment)))
      .join('/');
    return new RegExp(`^${pattern}(?:/|$)`).test(pathname);
  });
}

function escapeRegExp(value: string) {
  return value.replaceAll(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function canAccessNavigation(
  access: readonly ShigoNavigationAccess[] | undefined,
  context: ShigoNavigationAccessContext,
) {
  if (!access?.length) return true;

  return access.some(
    (requirement) =>
      (!requirement.contexts ||
        (context.dashboardContext !== null &&
          requirement.contexts.includes(context.dashboardContext))) &&
      (!requirement.permissions || requirement.permissions.every(context.can)) &&
      (!requirement.networkFeatures || requirement.networkFeatures.every(context.isFeatureAllowed)),
  );
}
